import React, { useEffect, useState, useCallback, useMemo } from "react";
import Button from "../components/ui/Button";
import { useToast } from "../hooks/useToast";
import { fetchStoresWithStaff, fetchCategories, fetchDiscountRules, fetchStoresWhereStaffAssigned, API_ROOT_PATH } from "../apiHelper";
import { Loader2, XCircle, Info, Percent, Tag, Link as LinkIcon } from 'lucide-react';
import DiscountRuleForm from "./discountmanager/DiscountRuleForm";
import { DataTable2 } from "../components/ui/DataTable2";
import DiscountLinkageManager from "./discountmanager/DiscountLinkageManager";
import { useNavigate } from "react-router-dom";


const DiscountManager = () => {
    const { showToast } = useToast();
    const navigate = useNavigate();

    const [user, setUser] = useState(null);
    const [activeView, setActiveView] = useState('dashboard'); // 'dashboard', 'discountRules', 'addEditRule', 'manageLinkages'
    const [stores, setStores] = useState([]);
    const [categories, setCategories] = useState([]);
    const [discountRules, setDiscountRules] = useState([]);
    const [editingRule, setEditingRule] = useState(null); // State to hold rule being edited
    const [linkingRuleId, setLinkingRuleId] = useState(null); // State to hold the ID of the rule being linked for linkage management

    const [loadingStores, setLoadingStores] = useState(true);
    const [errorStores, setErrorStores] = useState(null);
    const [loadingCategories, setLoadingCategories] = useState(true);
    const [errorCategories, setErrorCategories] = useState(null);
    const [loadingDiscountRules, setLoadingDiscountRules] = useState(true);
    const [errorDiscountRules, setErrorDiscountRules] = useState(null);

    // Pagination states for discount rules
    const [discountRulesOffset, setDiscountRulesOffset] = useState(0);
    const [discountRulesLimit] = useState(100); // Set a default limit
    const [hasMoreDiscountRules, setHasMoreDiscountRules] = useState(true);

    const [assignedStores, setAssignedStores] = useState([]);
    const [selectedStoreId, setSelectedStoreId] = useState(null);
    const [loadingAssignedStores, setLoadingAssignedStores] = useState(true);



    // Define DiscountTypeEnum and AppliesToEnum locally for now,
    // these would ideally come from a shared constants file or API
    const DiscountTypeEnum = {
        PERCENTAGE: "percentage",
        FIXED_AMOUNT: "fixed_amount",
        BUY_X_GET_Y: "buy_x_get_y",
        BUNDLE: "bundle",
        TIERED_PRICING: "tiered_pricing"
    };

    const AppliesToEnum = {
        ORDER: "order",
        PRODUCT: "product",
        CATEGORY: "category",
        SHIPPING: "shipping",
    };

    const ApprovalThresholdType = {
        NONE: "none",
        PERCENTAGE_OF_ITEM_VALUE: "percentage_of_item_value",
        FIXED_AMOUNT_OF_DISCOUNT: "fixed_amount_of_discount",
    };


    // Effect to load user from local storage
    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            showToast("Please log in to manage discounts.", "info");
            navigate('/login')
        } else {
            setUser(JSON.parse(lcUser));
        }
    }, [showToast]);

    // Fetch stores
    const loadStores = useCallback(async () => {
        if (!user) return;
        setLoadingStores(true);
        setErrorStores(null);
        try {
            // const fetchedStores = await fetchStoresWithStaff(user);
            const fetchedStores = await fetchStoresWhereStaffAssigned(user);
            // console.log(fetchedStores);
            setStores(fetchedStores);
        } catch (err) {
            const errorMessage = err.response?.data?.detail || `Failed to load stores: ${err.message || err}`;
            setErrorStores(errorMessage);
            showToast(errorMessage, 'error');
        } finally {
            setLoadingStores(false);
        }
    }, [user, showToast]);

    const loadAssignedStores = useCallback(async () => {
        if (!user) return;
        setLoadingAssignedStores(true);
        try {
            const fetchedAssignedStores = await fetchStoresWhereStaffAssigned(user);
            setAssignedStores(fetchedAssignedStores);
            if (fetchedAssignedStores.length > 0) {
                const initialId = fetchedAssignedStores[0].store_id;
                setSelectedStoreId(initialId);
                return initialId
            }
            return null;
        } catch (err) {
            const errorMessage = err.response?.data?.detail || `Failed to load assigned stores: ${err.message || err}`;
            showToast(errorMessage, 'error');
        } finally {
            setLoadingAssignedStores(false);
        }
    }, [user, showToast]);

    // Fetch categories
    const loadCategories = useCallback(async () => {
        if (!user) return;
        setLoadingCategories(true);
        setErrorCategories(null);
        try {
            const fetchedCategories = await fetchCategories(user);
            setCategories(fetchedCategories);
        } catch (err) {
            const errorMessage = err.response?.data?.detail || `Failed to load categories: ${err.message || err}`;
            setErrorCategories(errorMessage);
            showToast(errorMessage, 'error');
        } finally {
            setLoadingCategories(false);
        }
    }, [user, showToast]);

    // Fetch discount rules with pagination
    const loadDiscountRules = useCallback(async (offset = 0, limit = discountRulesLimit, append = false) => {
        if (!user) return;
        setLoadingDiscountRules(true);
        setErrorDiscountRules(null);
        try {
            const apiPayload = {
                users_id: user.users_id,
                logon_id: user.logon_id,
                hpwc: user.hpwc,
                offset: offset,
                limit: limit,
                store_id: selectedStoreId,
            };

            // console.log(apiPayload)
            const fetchedRules = await fetchDiscountRules(apiPayload);
            // console.log(fetchedRules)

            if (append) {
                setDiscountRules(prevRules => [...prevRules, ...fetchedRules]);
            } else {
                setDiscountRules(fetchedRules);
            }
            setHasMoreDiscountRules(fetchedRules.length === limit);

        } catch (err) {
            const errorMessage = err.response?.data?.detail || `Failed to load discount rules: ${err.message || err}`;
            setErrorDiscountRules(errorMessage);
            showToast(errorMessage, 'error');
            setDiscountRules([]);
            setHasMoreDiscountRules(false);
        } finally {
            setLoadingDiscountRules(false);
        }
    }, [user, showToast, discountRulesLimit, selectedStoreId]);

    // Initial load of data on user change
    // useEffect(() => {
    //     if (user) {
    //         loadStores();
    //         loadCategories();
    //         loadAssignedStores();
    //     }
    // }, [user, loadStores, loadCategories, loadAssignedStores]);
    useEffect(() => {
        const initializeData = async () => {
            if(!user) return;

            loadStores();
            loadCategories();

            const initialStoreId = await loadAssignedStores();

            setDiscountRulesOffset(0);
        };

        initializeData();
    }, [user, loadStores, loadCategories, loadAssignedStores]);

    useEffect(() => {
        if (user && assignedStores.length > 0) {
            setDiscountRulesOffset(0);
            loadDiscountRules(0, discountRulesLimit, false);
        }
    }, [selectedStoreId, discountRulesLimit]);

    const handleAddRule = () => {
        setEditingRule(null);
        setActiveView('addEditRule');
    };

    const handleEditRule = (rule) => {
        setEditingRule(rule);
        setActiveView('addEditRule');
    };

    const handleManageLinkages = (ruleId) => {
        setLinkingRuleId(ruleId);
        setActiveView('manageLinkages');
    };

    // New generic action handler for DataTable2
    const handleDataTableAction = (actionKey, row) => {
        switch (actionKey) {
            case 'manage_linkages':
                handleManageLinkages(row.discountrule_id);
                break;
            // Add other custom actions here if needed
            default:
                // Optionally handle unknown actions or log a warning
                break;
        }
    };

    const handleDeleteRule = async (ruleId) => {
        if (!window.confirm('Are you sure you want to delete this discount rule?')) {
            return;
        }
        try {
            // Assuming a delete API endpoint exists
            // await client.deleteData(`${API_ROOT_PATH}/discounts/delete-rule/${ruleId}`);
            showToast("Discount rule deleted successfully!", 'success');
            setDiscountRulesOffset(0);
            loadDiscountRules(0, discountRulesLimit, false);
        } catch (err) {
            const errorMessage = err.response?.data?.detail || `Failed to delete discount rule: ${err.message || err}`;
            showToast(errorMessage, 'error');
            console.error(errorMessage, err);
        }
    };

    const handleLoadMoreDiscountRules = () => {
        const newOffset = discountRulesOffset + discountRulesLimit;
        setDiscountRulesOffset(newOffset);
        loadDiscountRules(newOffset, discountRulesLimit, true);
    };

    const discountRuleColumns = useMemo(() => [
        { key: 'name', label: 'Rule Name' },
        { key: 'discount_type', label: 'Type', render: (type) => type.replace(/_/g, ' ').toUpperCase() },
        { key: 'value', label: 'Value', render: (value, row) => `${parseFloat(value).toFixed(2)}${row.discount_type === 'percentage' ? '%' : ''}` },
        { key: 'applies_to', label: 'Applies To', render: (appliesTo) => appliesTo.replace(/_/g, ' ').toUpperCase() },
        { key: 'is_active', label: 'Active', render: (isActive) => isActive ? 'Yes' : 'No' },
        { key: 'start_date', label: 'Start Date', render: (date) => new Date(date).toLocaleDateString() },
        { key: 'end_date', label: 'End Date', render: (date) => new Date(date).toLocaleDateString() },
        { key: 'requires_coupon_code', label: 'Coupon', render: (req) => req ? 'Yes' : 'No' },
        { key: 'coupon_code', label: 'Coupon Code', render: (code) => code || '-' },
    ], []);


    const renderContent = () => {
        if (loadingStores || loadingCategories || (loadingDiscountRules && discountRules.length === 0)) {
            return (
                <div className="flex items-center justify-center py-20">
                    <Loader2 size={48} className="animate-spin text-gmail-blue" />
                    <p className="text-xl text-text-secondary ml-4">Loading discount data...</p>
                </div>
            );
        }

        if (errorStores || errorCategories || errorDiscountRules) {
            return (
                <div className="flex flex-col items-center justify-center py-20 text-gmail-red">
                    <XCircle size={48} className="mb-4" />
                    <h3 className="text-2xl font-semibold mb-2">Error Loading Data</h3>
                    <p className="text-md text-center">{errorStores || errorCategories || errorDiscountRules}</p>
                    <Button onClick={() => { if (user) { loadStores(); loadCategories(); loadDiscountRules(0, discountRulesLimit, false); } }} variant="primary" className="mt-6">
                        Retry Loading
                    </Button>
                </div>
            );
        }

        if (!user) {
            return (
                <div className="flex flex-col items-center justify-center py-20 text-orange-600">
                    <Info size={48} className="mb-4" />
                    <h3 className="text-2xl font-semibold mb-2">Authentication Required</h3>
                    <p className="text-md text-center">Please log in to view and manage discount rules.</p>
                </div>
            );
        }

        switch (activeView) {
            case 'addEditRule':
                return (
                    <DiscountRuleForm
                        user={user}
                        stores={stores}
                        categories={categories}
                        DiscountTypeEnum={DiscountTypeEnum}
                        AppliesToEnum={AppliesToEnum}
                        ApprovalThresholdType={ApprovalThresholdType}
                        initialData={editingRule}
                        onSaveSuccess={() => {
                            setDiscountRulesOffset(0);
                            loadDiscountRules(0, discountRulesLimit, false);
                            setActiveView('discountRules');
                        }}
                        onCancel={() => setActiveView('discountRules')}
                    />
                );
            case 'manageLinkages':
                return (
                    <DiscountLinkageManager
                        user={user}
                        discountRuleId={linkingRuleId}
                        stores={stores}
                        categories={categories}
                        onClose={() => {
                            setActiveView('discountRules');
                            setLinkingRuleId(null);
                            loadDiscountRules(0, discountRulesLimit, false); // Refresh rules after managing linkages
                        }}
                    />
                );
            case 'discountRules':
            default:
                return (
                    <div className="p-6 bg-white rounded-lg shadow-md h-full flex flex-col">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8 pb-4 border-b border-gray-200">
                            {/* Left Side: Clean Title */}
                            <h2 className="text-2xl font-bold text-text-primary flex items-center gap-2">
                                <Tag size={24} className="text-gmail-blue" /> Manage Discount Rules
                            </h2>

                            {/* Right Side: Grouped Actions (Store Selector + Button) */}
                            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto">
                                {/* Minimalist Store Selector */}
                                <div className="relative flex items-center">
                                    <select
                                        id="store-select"
                                        value={selectedStoreId || ""}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setSelectedStoreId(val === "" ? null : parseInt(val, 10));
                                        }}
                                        className="appearance-none w-full sm:w-56 bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-700 font-medium text-sm rounded-lg pl-3 pr-8 py-2 cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-gmail-blue/20 focus:border-gmail-blue disabled:opacity-50"
                                        disabled={loadingAssignedStores}
                                    >
                                        <option value="">All Branches</option>
                                        {loadingAssignedStores ? (
                                            <option>Loading stores...</option>
                                        ) : (
                                            assignedStores.map((store) => (
                                                <option key={store.store_id} value={store.store_id}>
                                                    {store.identifier}
                                                </option>
                                            ))
                                        )}
                                    </select>
                                    {/* Custom chevron */}
                                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2.5 text-gray-400">
                                        <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                            <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                                        </svg>
                                    </div>
                                </div>

                                {/* Create Discount Button */}
                                <Button
                                    onClick={handleAddRule}
                                    variant="primary"
                                    className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium shadow-sm transition-all hover:shadow whitespace-nowrap"
                                >
                                    <Percent size={16} /> Add New Discount Rule
                                </Button>
                            </div>
                        </div>
                        <div className="flex-1 min-h-0">
                            <DataTable2
                                columns={discountRuleColumns}
                                data={discountRules}
                                emptyMessage="No discount rules configured yet. Click 'Add New Discount Rule' to get started!"
                                enableSearch={true}
                                onEdit={handleEditRule}
                                onDelete={(row) => handleDeleteRule(row.discountrule_id)}
                                onAction={handleDataTableAction} // Pass the new generic action handler
                                rowActions={[
                                    {
                                        label: "Manage Linkages",
                                        key: "manage_linkages", // The key will be passed to onAction
                                        icon: <LinkIcon size={16} />,
                                        // Removed onClick here as it's handled by onAction
                                    }
                                ]}
                            />
                            {hasMoreDiscountRules && (
                                <div className="flex justify-center mt-4">
                                    <Button
                                        onClick={handleLoadMoreDiscountRules}
                                        disabled={loadingDiscountRules}
                                        variant="secondary"
                                    >
                                        {loadingDiscountRules ? 'Loading More...' : 'Load More Discount Rules'}
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                );
        }
    };

    return (
        <div className="flex flex-col flex-1 bg-gray-100 min-h-screen">
            <div className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8 w-full">
                <h1 className="text-5xl font-extrabold text-center text-gmail-blue mb-10 leading-tight">
                    <Tag size={56} className="inline-block mr-4 align-middle" />
                    Discount Management
                </h1>

                {activeView !== 'dashboard' && (
                    <div className="mb-6">
                        <Button
                            variant="secondary"
                            onClick={() => setActiveView('dashboard')}
                            className="flex items-center gap-2 px-4 py-2 text-md"
                        >
                            &larr; Back to Dashboard
                        </Button>
                    </div>
                )}
                {renderContent()}
            </div>
        </div>
    );
};

export default DiscountManager;
